import { ChevronLeftIcon } from "lucide-react";
import { FC, useState } from "react";
import { useGetChat, useGetDoclist } from "~/api/chat/chat";
import { useGetProject } from "~/api/projects/projects";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { useChatDocumentParams } from "~/helpers";
import { Button } from "../../../../components/ui/button";
import { CaseInfoPopover } from "../modals/CaseInfoPopover";
import { PresenceAvatars } from "../presence/PresenceAvatars";
import { ChatTitle } from "./DocAreaChatTitle";

type DocAreaHeaderProps = {
  onNavigateBack: () => void;
};

export const DocAreaHeader: FC<DocAreaHeaderProps> = ({ onNavigateBack }) => {
  const handleGoBackToCase = () => {
    onNavigateBack();
  };

  const [caseInfoPopoverOpen, setCaseInfoPopoverOpen] = useState(false);

  const { organizationId, caseId, chatId, isViewOnly } =
    useChatDocumentParams();
  const { data: caseInfo } = useGetProject(organizationId, caseId);
  const { project: parentCase, isMaster: isMasterCase } = useParentCase();
  const { data: chatInfo, isLoading } = useGetChat(
    organizationId,
    caseId,
    chatId
  );
  const { data: doclist } = useGetDoclist(organizationId, caseId);

  const totalPages = doclist?.tree.reduce((acc, item) => {
    return acc + item.category.pageCount;
  }, 0);

  return (
    <div className="w-full bg-white border-b border-zinc-200 justify-between items-center inline-flex h-full">
      <div className="grow shrink basis-0 p-3 justify-between items-center flex max-w-[calc(100%-45px)]">
        <div className="justify-start items-center gap-3 flex w-full">
          <div className="h-8 w-8">
            <Button
              variant="secondary"
              size="icon"
              onClick={handleGoBackToCase}
              className="!h-8 !w-8 !p-0"
            >
              <ChevronLeftIcon className="h-[12.8px] w-[12.8px]" />
            </Button>
          </div>
          <div className="justify-between items-center gap-2 flex w-full">
            <div className="bg-white/0 justify-start items-center gap-2 flex max-w-full">
              <div
                className="truncate cursor-pointer max-w-[calc(100%)] inline-block text-zinc-900 text-lg font-semibold font-inter leading-7"
                onClick={() => setCaseInfoPopoverOpen(!caseInfoPopoverOpen)}
              >
                {isLoading ? (
                  <Skeleton className="h-4 w-[120px]" />
                ) : (
                  <ChatTitle
                    parentCase={parentCase}
                    isMasterCase={isMasterCase}
                    chatTitle={chatInfo?.title ?? "Untitled Chat"}
                  />
                )}
              </div>
              {caseInfo && parentCase && chatInfo && (
                <CaseInfoPopover
                  caseInfo={parentCase}
                  chatInfo={chatInfo}
                  open={caseInfoPopoverOpen}
                  totalPages={totalPages}
                  onOpenChange={setCaseInfoPopoverOpen}
                />
              )}
              {isViewOnly && (
                <Badge className="bg-blue-600 text-white rounded-full cursor-default hover:bg-blue-600">
                  View&nbsp;Only
                </Badge>
              )}
              <PresenceAvatars />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
